import React, { useRef, useState } from 'react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { staticImageAssets } from 'js/constants/Paths';

// import context
import { useData } from 'js/context/DataContext';

// import tracking
import { incrementDownloadClickCount, track, TRACKING_EVENTS } from 'js/utils/tracking';

gsap.registerPlugin(useGSAP);

const Modal = ({ modalType, toggleModal }) => {
  const modalRef = useRef(null);
  const containerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(modalType);
  const [activeModalType, setActiveModalType] = useState(modalType);

  useGSAP(
    () => {
      const modal = modalRef.current;
      const container = containerRef.current;

      if (!modal || !container) return;

      const timeline = gsap.timeline();

      if (modalType) {
        setActiveModalType(modalType);
        setIsVisible(true);

        timeline
          .set(modal, {
            display: 'flex',
            zIndex: 99999,
            backgroundColor: 'rgba(0, 0, 0, 0)',
            overflowY: 'hidden',
          })
          .set(container, { y: '100vh' })
          .to(modal, {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            duration: 0.2,
            ease: 'power1.out',
          })
          .to(container, {
            y: 0,
            duration: 0.75,
            ease: 'power3.out',
          })
          .call(() => {
            gsap.set(modal, { overflowY: 'auto' });
          });
      } else if (isVisible) {
        timeline
          .set(modal, {
            overflowY: 'hidden',
          })
          .to(container, {
            y: '100vh',
            duration: 0.25,
            ease: 'power3.in',
          })
          .to(modal, {
            backgroundColor: 'rgba(0, 0, 0, 0)',
            duration: 0.2,
            ease: 'power1.in',
          })
          .set(modal, {
            display: 'none',
            zIndex: -1,
          })
          .call(() => {
            setIsVisible(false);
            setActiveModalType(false);
          });
      }

      return () => {
        timeline.kill();
      };
    },
    { dependencies: [modalType], scope: modalRef }
  );

  return (
    <section ref={modalRef} className={'modal' + (isVisible ? ' modal--active' : '')}>
      <div className="modal__container" ref={containerRef}>
        <button
          className="modal__close-button"
          onClick={() => {
            toggleModal(false);
          }}
        >
          <img src={staticImageAssets('images/static/btn_close.png')} />
        </button>
        {activeModalType == 'video' && <YoutubePlayer />}
        {activeModalType == 'downloads' && <Downloads />}
      </div>
    </section>
  );
};

const YoutubePlayer = () => {
  const { settings: { trailer_video: { youtube_video_id = '' } = {} } = {} } = useData();

  return (
    <div className="youtube-player">
      <iframe
        width="100%"
        height="100%"
        src={`https://www.youtube-nocookie.com/embed/${youtube_video_id}&rel=0&modestbranding=1&cc_load_policy=0&controls=1`}
        frameBorder="0"
        allow="web-share"
        referrerPolicy="strict-origin-when-cross-origin"
        allowFullScreen
      ></iframe>
    </div>
  );
};

const Downloads = () => {
  const { downloads = [] } = useData();

  const onDownloadClick = (item) => {
    const { file_type, image, pdf } = item;
    const itemLink = file_type == 'image' ? image : pdf;
    const downloadClickCount = incrementDownloadClickCount();

    track(TRACKING_EVENTS.CLICK_EXIT.id, {
      item_link: itemLink,
      item_label: downloadClickCount,
    });
  };

  return (
    <div className="downloads">
      {downloads.map((item, index) => {
        const { file_type, image, pdf, thumbnail_image } = item;
        return (
          <div className="downloads__item" key={`downlaod-item_${index}`}>
            <a
              href={file_type == 'image' ? image : pdf}
              target="_blank"
              rel="noreferrer"
              onClick={() => onDownloadClick(item)}
            >
              <img src={thumbnail_image} />
            </a>
          </div>
        );
      })}
    </div>
  );
};

export default Modal;
